import {
  Affix,
  Card,
  Col,
  Collapse,
  Row,
  Spin,
  Typography,
  message,
} from "antd";
import dayjs from "dayjs";
import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import { FilterOutlined } from "@ant-design/icons";
import { getData } from "apiServices/common";
import { isMobileView } from "../../Common/AppHelper";
import Header from "../../Common/Header";
import OptimizedCalendar from "./Calendar";
import Filter from "./Filter";
import HeaderDescription from "./HeaderDescription";
import "./index.css";

// ==================== CONSTANTS ====================
const API_ENDPOINTS = {
  PLAN_INITIATE_LIST: "/api/productionPlanning/plan_initiate/list",
  CALENDAR_HEADER: "/api/productionPlanning/weekend/calendar-header",
};

const LOADING_STATES = {
  IDLE: "idle",
  LOADING: "loading",
  SUCCESS: "success",
  ERROR: "error",
};

// ==================== CUSTOM HOOKS ====================
const useCalendarData = (date) => {
  const [calendarData, setCalendarData] = useState(null);
  const [loadingState, setLoadingState] = useState(LOADING_STATES.IDLE);
  const [error, setError] = useState(null);

  const fetchCalendarData = useCallback(async () => {
    if (!date) return;

    setLoadingState(LOADING_STATES.LOADING);
    setError(null);
    setCalendarData(null);

    try {
      const startDate = dayjs(date).startOf("month").format("YYYY-MM-DD");
      const endDate = dayjs(date).endOf("month").format("YYYY-MM-DD");

      const urlParams = new URLSearchParams({
        startDate,
        endDate,
      });

      const [calendarHeaderResponse, planInitiateResponse] = await Promise.all([
        getData(`${API_ENDPOINTS.CALENDAR_HEADER}?${urlParams}`),
        getData(`${API_ENDPOINTS.PLAN_INITIATE_LIST}?${urlParams}`),
      ]);

      // Validate responses
      if (!calendarHeaderResponse?.data?.data) {
        throw new Error("Failed to fetch calendar header data");
      }

      if (!planInitiateResponse?.data?.data) {
        throw new Error("Failed to fetch plan initiate data");
      }

      const calendarHeader = calendarHeaderResponse.data.data;
      const planInitiateList = planInitiateResponse.data.data;

      // Validate data structure
      if (!Array.isArray(planInitiateList) || planInitiateList.length === 0) {
        setCalendarData({
          calendarHeader,
          calendarBody: [],
        });
        setLoadingState(LOADING_STATES.SUCCESS);
        return;
      }

      setCalendarData({
        calendarHeader,
        calendarBody: planInitiateList,
      });
      setLoadingState(LOADING_STATES.SUCCESS);
    } catch (err) {
      console.error("Error fetching calendar data:", err);
      setError(err.message || "Failed to load calendar data");
      setLoadingState(LOADING_STATES.ERROR);
      message.error("Failed to load calendar data. Please try again.");
    }
  }, [date]);

  return {
    calendarData,
    setCalendarData,
    loadingState,
    error,
    refetch: fetchCalendarData,
  };
};

// ==================== COMPONENTS ====================
const LoadingSpinner = React.memo(() => (
  <div
    style={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: "50vh",
    }}
  >
    <Spin size="large" />
  </div>
));

const ErrorDisplay = React.memo(({ error, onRetry }) => (
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      height: "50vh",
      gap: "16px",
    }}
  >
    <Typography.Text type="danger">
      {error || "Something went wrong"}
    </Typography.Text>
    {onRetry && (
      <button
        onClick={onRetry}
        style={{
          padding: "8px 16px",
          backgroundColor: "#1890ff",
          color: "white",
          border: "none",
          borderRadius: "6px",
          cursor: "pointer",
        }}
      >
        Retry
      </button>
    )}
  </div>
));

const CalendarContainer = React.memo(
  ({ calendarData, setCalendarData, refetch, loadingState, error }) => {
    const renderContent = () => {
      switch (loadingState) {
        case LOADING_STATES.LOADING:
          return <LoadingSpinner />;

        case LOADING_STATES.ERROR:
          return <ErrorDisplay error={error} onRetry={() => refetch()} />;

        case LOADING_STATES.SUCCESS:
          if (!calendarData) {
            return (
              <ErrorDisplay
                error="No calendar data available"
                onRetry={() => refetch()}
              />
            );
          }

          return (
            <OptimizedCalendar
              calendarData={calendarData}
              setCalendarData={setCalendarData}
              refetch={refetch}
            />
          );

        default:
          return <LoadingSpinner />;
      }
    };

    return (
      <div
        style={{
          width: "100%",
          overflowX: "auto",
          overflowY: "auto",
          maxHeight: "calc(100vh - 100px)",
          whiteSpace: "nowrap",
        }}
      >
        <div style={{ minWidth: "1500px" }}>{renderContent()}</div>
      </div>
    );
  },
);

// ==================== MAIN COMPONENT ====================
const ProductionPlanningDashboard = ({ width }) => {
  const [date, setDate] = useState(() => dayjs().format("YYYY-MM"));

  const { calendarData, setCalendarData, loadingState, error, refetch } =
    useCalendarData(date);

  // Initialize data on mount and date change
  useEffect(() => {
    refetch();
  }, [refetch]);

  // Memoized card width calculation
  const cardWidth = useMemo(
    () => (isMobileView() ? "100%" : `${width}px`),
    [width],
  );

  return (
    <Fragment>
      {/* Fixed Header */}
      <Affix offsetTop={0}>
        <Card
          className="p-fixed"
          style={{
            width: cardWidth,
            zIndex: 100,
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          }}
        >
          <Header
            title={
              <Typography.Title level={5} style={{ margin: 0 }}>
                Production Planning Dashboard
              </Typography.Title>
            }
          />
        </Card>
      </Affix>

      {/* Main Content */}
      <Card
        className="production-planning-dashboard"
        style={{
          padding: "0px 4px",
          marginTop: "2px",
        }}
      >
        {/* Filter Section */}
        <div>
          <Collapse>
            <Collapse.Panel
              header={
                <div style={{ display: "flex", alignItems: "center" }}>
                  <FilterOutlined style={{ marginRight: 10 }} />
                  <span>Production Planning Settings & Filters</span>
                </div>
              }
              key="1"
            >
              <Row gutter={24}>
                <Col span={16} className="header-description">
                  <HeaderDescription />
                </Col>
                <Col span={8} className="filter-container">
                  <Filter setDate={setDate} />
                </Col>
              </Row>
            </Collapse.Panel>
          </Collapse>
        </div>

        {/* Calendar Section */}
        <Row gutter={24} style={{ marginTop: "16px" }}>
          <Col span={24}>
            <CalendarContainer
              calendarData={calendarData}
              setCalendarData={setCalendarData}
              refetch={refetch}
              loadingState={loadingState}
              error={error}
            />
          </Col>
        </Row>
      </Card>
    </Fragment>
  );
};

export default ProductionPlanningDashboard;

// ==================== UTILITY EXPORTS ====================
export const cardStyle = {
  borderRadius: "10px",
  padding: "10px",
  height: "100%",
  width: "100%",
  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
  transition: "box-shadow 0.3s ease",
};

// Sample data structure for reference (can be moved to a separate file)
export const sampleCalendarBody = [
  {
    line_id: 20,
    line_name: "Line-1",
    dates: [
      {
        date: "2025-07-01",
        week_status: "unplanned",
        is_full_day_planned: false,
        is_droppable: true,
        plans: [],
      },
      // ... more dates
    ],
  },
  // ... more lines
];
